import AutocompleteInput from 'components/Autocomplete/AutocompleteInput';
import Button from 'tailwind-components/Button/Button';
import Card from 'components/Card/Card';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import withLocalLoader from 'components/HOCs/withLocalLoader';
import { Play } from 'lucide-react';
import { CircularProgress } from '@mui/material';
import { USER_TYPES } from 'constants/enum';
import buildStatementCarrierOptions from 'helpers/commissions/statementCarrierOptions';
import { useDispatch, useSelector } from 'react-redux';

import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField
} from '@mui/material';
import {
  getAppointmentsViewData,
  downloadCommissionsData,
  getSpecificAgencyData,
  getSelectedAgenciesAgents,
  getStatementsPeriods
} from 'state/actions/actions';

const INITIAL_VALUES = {
  periods: [],
  carriers: [],
  agencies: [],
  agents: [],
  orderBy: 'Carrier',
  includeAllCarriers: true
};

const isLOA = obj => {
  const compLevel =
    obj['Custom_Comp_Level'] !== null
      ? obj['Custom_Comp_Level']
      : obj['Comp_LevelsX'];
  return compLevel === 'LOA';
};

const showAsPayeeAgent = obj => {
  return obj['Force_Show_as_Payee_Agent'] ?? false;
};

const showAsPayeeAgency = obj => {
  return obj['forceShowAsPayeeAgency'] ?? false;
};

const StatementsView = ({ setLocalLoader, localLoader }) => {
  //Hoooks
  const dispatch = useDispatch();
  const cancelTokenSource = axios.CancelToken.source();
  // States from redux
  const { satementsCarriersArray } = useSelector(state => state.appointments);
  const { agencyInformation } = useSelector(
    state => state.agencyManagement.agencyData
  );
  const { statementPeriods } = useSelector(state => state.appointments);
  const { zohoData } = useSelector(state => state.user);
  const { isDownloadingReport } = useSelector(state => state.commissions);
  const currentAgenciesAgentsDict = useSelector(
    state => state.agencyManagement.comissions_tab_agents
  );
  // Local states
  const [formData, setformData] = useState(INITIAL_VALUES);
  // Mulstiselect options
  const notAdminAndLOA = React.useMemo(
    () => zohoData.Position_at_Agency !== USER_TYPES.ADMIN && isLOA(zohoData),
    [zohoData]
  );
  const groupedPeriodOptions = React.useMemo(() => {
    return statementPeriods.map(periodObj => {
      const [periodMonth, , periodYear] = periodObj.name.split('/');
      const monthsNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ];
      return {
        ...periodObj,
        month: `${monthsNames[Number(periodMonth) - 1]} ${periodYear}`
      };
    });
  }, [statementPeriods]);

  const carriersOptions = React.useMemo(
    () => buildStatementCarrierOptions(satementsCarriersArray),
    [satementsCarriersArray]
  );
  const [allAgenciesAreLoa, setAllAgenciesAreLoa] = useState(false);
  const agenciesOptions = React.useMemo(() => {
    // If user is not admin, don't compute any of this
    if (zohoData.Position_at_Agency !== USER_TYPES.ADMIN) return [];

    // Preselects agency
    if (agencyInformation?.id?._value && agencyInformation?.name?._value) {
      setformData(prevState => ({
        ...prevState,
        ['agencies']: [
          {
            id: agencyInformation?.id?._value,
            name: agencyInformation?.name?._value
          }
        ]
      }));
    }
    const rawAgenciesOptions =
      agencyInformation.Downline_Agencies_Three?.concat([agencyInformation]);
    const filteredAgenciesOptions = rawAgenciesOptions?.filter(
      f => f.Comm_Level_Tier_Auto !== 'LOA' || showAsPayeeAgency(f)
    );
    if (
      rawAgenciesOptions?.length !== 0 &&
      filteredAgenciesOptions?.length === 0
    ) {
      setAllAgenciesAreLoa(true);
    }
    const returnAgenciesOptions =
      filteredAgenciesOptions?.map(agency => ({
        id: agency.id._value,
        name: agency.name._value
      })) || [];

    // Gets all the agents from all the agencies from the tree
    if (zohoData.Position_at_Agency === USER_TYPES.ADMIN) {
      const ids = [...returnAgenciesOptions?.map(agency => agency.id)];
      dispatch(
        getSelectedAgenciesAgents(
          () => {},
          currentAgenciesAgentsDict,
          ids,
          cancelTokenSource
        )
      );
    }

    return returnAgenciesOptions;
  }, [agencyInformation.Downline_Agencies_Three]);
  const mainAgentsOptions = React.useMemo(() => {
    const mainOptions =
      zohoData.NPN !== null &&
      zohoData.NPN !== 0 &&
      zohoData.NPN !== '' &&
      zohoData.NPN !== '0' &&
      zohoData.NPN !== 1 &&
      zohoData.NPN !== '1' &&
      zohoData.NPN.toString().length > 4 &&
      zohoData.NPN.toString().length <= 9
        ? [
            {
              type: 'Main Agent',
              name: zohoData.First_Name + ' ' + zohoData.Last_Name,
              npn: zohoData.NPN,
              Agency_Name: 'Main Agent',
              Agency_Id: zohoData.Account_Name?.id,
              id: zohoData?.id
            }
          ]
        : [];
    return mainOptions;
  }, [zohoData.NPN, zohoData.First_Name, zohoData.Last_Name, zohoData.id]);

  const [legitHasNoAgents, setLegitHasNoAgents] = useState(false);

  const agentsOptions = React.useMemo(() => {
    const selectedAgenciesAgents = Object.keys(currentAgenciesAgentsDict)
      .map(selected_agency_id => currentAgenciesAgentsDict[selected_agency_id])
      .filter(x => x)
      .flat()
      .filter(option => showAsPayeeAgent(option) || !isLOA(option))
      .map(option => {
        return {
          type: 'Downline Agent',
          name: option.First_Name + ' ' + option.Last_Name,
          npn: option.NPN,
          Agency_Id: option.Agency_Id,
          Agency_Name: option.Agency,
          id: option.id
        };
      })
      .filter(
        option =>
          option.npn !== null &&
          option.npn !== 0 &&
          option.npn !== '' &&
          option.npn !== '0' &&
          option.npn !== 1 &&
          option.npn !== '1' &&
          option.npn.toString().length > 4 &&
          option.npn.toString().length <= 9
      )
      .filter(option => option.npn !== zohoData.NPN);
    if (
      Object.keys(currentAgenciesAgentsDict).length === 0 &&
      selectedAgenciesAgents.length === 0
    )
      setLegitHasNoAgents(true);
    // If user is LOA, return only other agents
    if (isLOA(zohoData) && !showAsPayeeAgent(zohoData))
      return selectedAgenciesAgents;
    // If user is not LOA, returns itself as well.
    return [...mainAgentsOptions, ...selectedAgenciesAgents];
  }, [currentAgenciesAgentsDict, mainAgentsOptions, zohoData]);

  // Effects
  // Gets data from backend
  useEffect(() => {
    const getData = async () => {
      setLocalLoader(true);
      dispatch(getStatementsPeriods());
      dispatch(
        getAppointmentsViewData(
          () => {},
          () => {},
          true
        )
      );
      if (zohoData.Position_at_Agency !== USER_TYPES.ADMIN) return;
      dispatch(
        getSpecificAgencyData(
          () => {},
          zohoData?.Account_Name?.id,
          () => {}
        )
      );

      // return () => {
      //   cancelTokenSource.cancel('API Call Canceled');
      // };
    };
    getData();
  }, []);

  // Preselects main agent
  useEffect(() => {
    // Preselects the logged in agent if not LOA
    if (isLOA(zohoData) && !showAsPayeeAgent(zohoData)) return;
    setformData(prevData => ({
      ...prevData,
      agents: mainAgentsOptions
    }));
  }, [mainAgentsOptions]);

  // If all data from all sources has finished loading, turns off the local loader
  useEffect(() => {
    if (notAdminAndLOA) return setLocalLoader(false);
    const statementPeriodsAreLoaded = statementPeriods.length > 0;
    const carriersOptionsAreLoaded = carriersOptions.length > 1;
    const agenciesOptionsAreLoaded =
      agenciesOptions.length > 0 ||
      allAgenciesAreLoa ||
      zohoData.Position_at_Agency !== USER_TYPES.ADMIN;
    const agentsOptionsAreLoaded = agentsOptions.length > 0 || legitHasNoAgents;
    if (
      statementPeriodsAreLoaded &&
      carriersOptionsAreLoaded &&
      agenciesOptionsAreLoaded &&
      agentsOptionsAreLoaded
    ) {
      setLocalLoader(false);
    }
  }, [statementPeriods, carriersOptions, agenciesOptions, agentsOptions]);
  const handleChangeSelectValue = (values, property) => {
    setformData(prevState => ({
      ...prevState,
      [property]: values
    }));
  };

  const handleRunReport = () => {
    dispatch(downloadCommissionsData(formData, carriersOptions));
  };

  const handleChangeIncludeAllCarriers = () => {
    setformData(prevState => ({
      ...prevState,
      includeAllCarriers: !prevState.includeAllCarriers,
      carriers: []
    }));
  };

  const handleResetValues = () => {
    setformData(INITIAL_VALUES);
  };
  // Validations
  const agentOrAgencySelected = React.useMemo(
    () => formData.agents.length === 0 && formData.agencies.length === 0,
    [formData.agents, formData.agencies]
  );

  const validateReportData = React.useMemo(
    () =>
      !formData.orderBy ||
      agentOrAgencySelected ||
      (formData.carriers.length === 0 && !formData.includeAllCarriers) ||
      formData.periods.length === 0 ||
      isDownloadingReport,
    [
      formData.orderBy,
      formData.carriers,
      formData.includeAllCarriers,
      formData.periods,
      agentOrAgencySelected,
      isDownloadingReport
    ]
  );

  return (
    <Card fill container title='Run Commission Statements' titleType='primary'>
      {notAdminAndLOA ? (
        <h4>
          Your commission level is LOA, please contact the admin at your upline
          agency for your commission statements.
        </h4>
      ) : (
        <>
          <h4>
            The statement for the selected period will be downloaded as a CSV
            after hitting {'Run report'}.
          </h4>
          <FormGroup>
            <Stack mb={2}>
              <AutocompleteInput
                showScrollbar
                multiple
                options={groupedPeriodOptions}
                groupBy={option => option.month}
                selectAllByGroup={true}
                id='monthandyear'
                value={formData.periods}
                isOptionEqualToValue={(option, value) =>
                  option.name === value.name
                }
                getOptionLabel={option => option.name}
                onValueChanges={e => handleChangeSelectValue(e, 'periods')}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={'Period'}
                    placeholder={'Select periods'}
                    error={formData.periods.length === 0}
                    helperText={
                      formData.periods.length === 0
                        ? 'You need to select at least 1 period'
                        : ''
                    }
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                )}
              />
            </Stack>

            <Stack mb={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={handleChangeIncludeAllCarriers}
                    checked={formData.includeAllCarriers}
                  />
                }
                label={'Include All Carriers'}
              />
            </Stack>

            {!formData.includeAllCarriers ? (
              <Stack mb={3}>
                <AutocompleteInput
                  showScrollbar
                  multiple
                  loading={localLoader}
                  options={carriersOptions
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .sort((a, b) => -b.type.localeCompare(a.type))}
                  isOptionEqualToValue={(option, value) =>
                    option.name === value.name
                  }
                  groupBy={option => option.type}
                  getOptionLabel={option => option.name}
                  id='carriers'
                  value={formData.carriers}
                  onValueChanges={e => handleChangeSelectValue(e, 'carriers')}
                  disabled={formData.includeAllCarriers}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={
                        formData.includeAllCarriers
                          ? 'All Carrier(s)'
                          : 'Carrier(s)'
                      }
                      placeholder={'Select carriers'}
                      error={
                        formData.carriers.length === 0 &&
                        !formData.includeAllCarriers
                      }
                      helperText={
                        formData.carriers.length === 0 &&
                        !formData.includeAllCarriers
                          ? 'You need to select at least 1 carrier'
                          : ''
                      }
                      InputLabelProps={{
                        shrink: !formData.includeAllCarriers
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {localLoader ? (
                              <CircularProgress color='inherit' size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        )
                      }}
                    />
                  )}
                />
              </Stack>
            ) : null}
            {zohoData.Position_at_Agency === USER_TYPES.ADMIN && (
              <Stack mb={3}>
                <AutocompleteInput
                  showScrollbar
                  multiple
                  loading={localLoader}
                  loadingBlocksOptions={true}
                  options={agenciesOptions.sort((a, b) =>
                    a.name.localeCompare(b.name)
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.name === value.name
                  }
                  getOptionLabel={option => option.name}
                  id='agencies'
                  value={formData.agencies}
                  onValueChanges={e => {
                    handleChangeSelectValue(e, 'agencies');
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={'Payee Agency(s)'}
                      placeholder={'Select agencies'}
                      error={agentOrAgencySelected}
                      helperText={
                        agentOrAgencySelected
                          ? 'You need to select at least 1 agency or 1 agent'
                          : ''
                      }
                      InputLabelProps={{
                        shrink: true
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {localLoader ? (
                              <CircularProgress color='inherit' size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        )
                      }}
                    />
                  )}
                />
              </Stack>
            )}

            {zohoData.Position_at_Agency === USER_TYPES.ADMIN && (
              <Stack mb={3}>
                <AutocompleteInput
                  showScrollbar
                  label={'agents'}
                  multiple
                  selectAll
                  loading={localLoader}
                  options={localLoader ? [] : agentsOptions}
                  isOptionEqualToValue={(option, value) =>
                    option.name === value.name
                  }
                  groupBy={option => option.Agency_Name}
                  getOptionLabel={option => option.name}
                  id='downlineagents'
                  value={localLoader ? [] : formData.agents}
                  onValueChanges={e => handleChangeSelectValue(e, 'agents')}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={'Payee Agent(s)'}
                      placeholder={'Select agents'}
                      error={agentOrAgencySelected}
                      helperText={
                        agentOrAgencySelected
                          ? 'You need to select at least 1 agency or 1 agent'
                          : ''
                      }
                      InputLabelProps={{
                        shrink: true
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {localLoader ? (
                              <CircularProgress color='inherit' size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        )
                      }}
                    />
                  )}
                  selectAllByGroup={true}
                />
              </Stack>
            )}

            <Stack direction={'row'} spacing={2}>
              <Button
                text='Run report'
                variant='primaryDarkened'
                rounded='small'
                icon={<Play size={20} />}
                onClick={handleRunReport}
                disabled={validateReportData}
                isLoading={isDownloadingReport}
              />
              <Button
                text='Reset form'
                variant='default'
                outlined
                rounded='small'
                onClick={handleResetValues}
              />
            </Stack>
          </FormGroup>
        </>
      )}
    </Card>
  );
};

StatementsView.propTypes = {
  localLoader: PropTypes.bool,
  setLocalLoader: PropTypes.func
};

export default withLocalLoader(
  StatementsView,
  true,
  'Commissions',
  'Statements'
);
